/* 
Generated code: do not modify this file as it will be overwritten
Template: ImmutableJSRecord.cst

Known Issues/TODO:
- Using *Props type as Record constructor parameter is insufficient.
If a property needs to be a Record type, it should already be constructed.
- Rename Record fromJS factory method to fromJSON
- Create parseJSON method that translates JSON to Props



 */

/* eslint-disable import/order */

import { createSelector } from '@ngrx/store';
import { List, Map, Record, Seq, Set } from 'immutable';
import {
  MaintenanceParametersRecord,
  MaintenanceParametersProps,
} from 'src/store/generated/maintenance-parameters-record.generated';
import {
  DeferralDueDateRecord,
  DeferralDueDateProps,
} from 'src/store/generated/deferral-due-date-record.generated';
import { NextDueRecord, NextDueProps } from 'src/store/generated/next-due-record.generated';
import { AppState } from 'src/store/appstate';
import { getFromOrThrow, propValueOrDefault, tryOrDefault } from 'src/util/values';

export interface VehicleMaintenanceProps {
  id: string;
  maintenanceParams: MaintenanceParametersProps | null;
  deferralDueDates: List<DeferralDueDateProps>;
  nextDues: List<NextDueProps>;
  notes: string | null;
  isReleasedFromOCF: boolean | null;
  vehicleId: string | null;
  companyId: string;
  dateLastUpdated: Date;
  dateCreated: Date;
  dateDeleted: Date | null;
  lastUpdatedById: string | null;
}

export class VehicleMaintenanceRecord extends Record<VehicleMaintenanceProps>({
  id: null as any,
  maintenanceParams: null as any,
  deferralDueDates: null as any,
  nextDues: null as any,
  notes: null as any,
  isReleasedFromOCF: null as any,
  vehicleId: null as any,
  companyId: null as any,
  dateLastUpdated: null as any,
  dateCreated: null as any,
  dateDeleted: null as any,
  lastUpdatedById: null as any,
}) {
  public static fromJS(json: {
    id: string;
    maintenanceParams: any;
    deferralDueDates: any;
    nextDues: any;
    notes: string | null;
    isReleasedFromOCF: boolean | null;
    vehicleId: string | null;
    companyId: string;
    dateLastUpdated: string | number | Date;
    dateCreated: string | number | Date;
    dateDeleted: string | number | Date | null;
    lastUpdatedById: string | null;
  }): VehicleMaintenanceRecord {
    if (json == null) {
      throw new Error('VehicleMaintenanceRecord.fromJS: parameter cannot be null');
    }
    return new VehicleMaintenanceRecord({
      id: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'id', '00000000-0000-0000-0000-000000000000'),
        'VehicleMaintenanceJson.id',
      ),
      maintenanceParams:
        json.maintenanceParams != null
          ? new MaintenanceParametersRecord(json.maintenanceParams)
          : null,
      deferralDueDates: List<DeferralDueDateRecord>(
        getFromOrThrow(
          json,
          (j) => j.deferralDueDates,
          'VehicleMaintenanceJson.deferralDueDates',
        ).map((o: any) => new DeferralDueDateRecord(o)),
      ),
      nextDues: List<NextDueRecord>(
        getFromOrThrow(json, (j) => j.nextDues, 'VehicleMaintenanceJson.nextDues').map(
          (o: any) => new NextDueRecord(o),
        ),
      ),
      notes: json.notes,
      isReleasedFromOCF: json.isReleasedFromOCF,
      vehicleId: json.vehicleId,
      companyId: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'companyId', '00000000-0000-0000-0000-000000000000'),
        'VehicleMaintenanceJson.companyId',
      ),
      dateLastUpdated: new Date(json.dateLastUpdated),
      dateCreated: new Date(json.dateCreated),
      dateDeleted: json.dateDeleted != null ? new Date(json.dateDeleted) : null,
      lastUpdatedById: json.lastUpdatedById,
    });
  }

  public static fromJSUnsafe(json: {
    id: string;
    maintenanceParams: any;
    deferralDueDates: any;
    nextDues: any;
    notes: string | null;
    isReleasedFromOCF: boolean | null;
    vehicleId: string | null;
    companyId: string;
    dateLastUpdated: string | number | Date;
    dateCreated: string | number | Date;
    dateDeleted: string | number | Date | null;
    lastUpdatedById: string | null;
  }): [VehicleMaintenanceRecord, Error[]] {
    if (json == null) {
      throw new Error('VehicleMaintenanceRecord.fromJSUnsafe: parameter cannot be null');
    }
    const errors: Error[] = [];

    const record = new VehicleMaintenanceRecord({
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      id: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'id', '00000000-0000-0000-0000-000000000000'),
            'VehicleMaintenanceJson.id',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      maintenanceParams: tryOrDefault(
        () =>
          json.maintenanceParams != null
            ? new MaintenanceParametersRecord(json.maintenanceParams)
            : null,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      deferralDueDates: tryOrDefault(
        () =>
          List<DeferralDueDateRecord>(
            getFromOrThrow(
              json,
              (j) => j.deferralDueDates,
              'VehicleMaintenanceJson.deferralDueDates',
            ).map((o: any) => new DeferralDueDateRecord(o)),
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      nextDues: tryOrDefault(
        () =>
          List<NextDueRecord>(
            getFromOrThrow(json, (j) => j.nextDues, 'VehicleMaintenanceJson.nextDues').map(
              (o: any) => new NextDueRecord(o),
            ),
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      notes: tryOrDefault(
        () => json.notes,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      isReleasedFromOCF: tryOrDefault(
        () => json.isReleasedFromOCF,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      vehicleId: tryOrDefault(
        () => json.vehicleId,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      companyId: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'companyId', '00000000-0000-0000-0000-000000000000'),
            'VehicleMaintenanceJson.companyId',
          ),
        (err) => errors.push(err),
      )!,
      dateLastUpdated: new Date(json.dateLastUpdated),
      dateCreated: new Date(json.dateCreated),
      dateDeleted: json.dateDeleted != null ? new Date(json.dateDeleted) : null,
      lastUpdatedById: json.lastUpdatedById,
    });

    return [record, errors];
  }

  constructor(props: VehicleMaintenanceProps) {
    super(props);
    if (props == null) {
      throw new Error('VehicleMaintenanceRecord constructor parameter cannot be null');
    }
  }
}

export abstract class VehicleMaintenanceViewModelBase implements VehicleMaintenanceProps {
  protected readonly _props: VehicleMaintenanceProps;
  private _json?: VehicleMaintenanceProps;

  get id(): string {
    return this._props.id;
  }
  get maintenanceParams(): MaintenanceParametersProps | null {
    return this._props.maintenanceParams;
  }
  get deferralDueDates(): List<DeferralDueDateProps> {
    return this._props.deferralDueDates;
  }
  get nextDues(): List<NextDueProps> {
    return this._props.nextDues;
  }
  get notes(): string | null {
    return this._props.notes;
  }
  get isReleasedFromOCF(): boolean | null {
    return this._props.isReleasedFromOCF;
  }
  get vehicleId(): string | null {
    return this._props.vehicleId;
  }
  get companyId(): string {
    return this._props.companyId;
  }

  get dateCreated(): Date {
    return this._props.dateCreated;
  }
  get dateLastUpdated(): Date {
    return this._props.dateLastUpdated;
  }
  get dateDeleted(): Date | null {
    return this._props.dateDeleted;
  }
  get lastUpdatedById(): string | null {
    return this._props.lastUpdatedById;
  }

  constructor(props: VehicleMaintenanceProps) {
    this._props = props;
  }

  public toJS() {
    return this.toJSON();
  }

  public toJSON() {
    if (this._json == null) {
      this._json = {
        id: this._props.id,
        maintenanceParams: this._props.maintenanceParams,
        deferralDueDates: this._props.deferralDueDates,
        nextDues: this._props.nextDues,
        notes: this._props.notes,
        isReleasedFromOCF: this._props.isReleasedFromOCF,
        vehicleId: this._props.vehicleId,
        companyId: this._props.companyId,
        dateLastUpdated: this._props.dateLastUpdated,
        dateCreated: this._props.dateCreated,
        dateDeleted: this._props.dateDeleted,
        lastUpdatedById: this._props.lastUpdatedById,
      };
    }
    return this._json;
  }
}

//#region Selectors

export const selectAllVehicleMaintenanceEntities = (state: AppState) =>
  state.entityStores.vehicleMaintenance.entities;

export const selectVehicleMaintenanceById = (id: string) => (state: AppState) =>
  state.entityStores.vehicleMaintenance.entities.get(id);

export const selectVehicleMaintenanceMetadataById = (id: string) => (state: AppState) =>
  state.entityStores.vehicleMaintenance.meta.get(id);

export const selectVehicleMaintenanceAndMetadataById = (id: string) =>
  createSelector(
    selectVehicleMaintenanceById(id),
    selectVehicleMaintenanceMetadataById(id),
    (vehicleMaintenance, meta) => ({ vehicleMaintenance, meta }),
  );

export function selectVehicleMaintenanceEntitiesByIds(ids: string[]) {
  return createSelector(
    selectAllVehicleMaintenanceEntities,

    (entities) => {
      // TODO: Once we're on TS 3.5+, I don't think we'll need use type assertions
      const entries = ids.map((id) => [id, entities.get(id)]) as unknown as Iterable<
        [string, VehicleMaintenanceRecord | undefined]
      >;
      return Map(entries);
    },
  );
}

export function selectVehicleMaintenanceEntitiesByNotes(notes: string) {
  return createSelector(selectAllVehicleMaintenanceEntities, (entities) =>
    entities.filter((e) => e.notes === notes).valueSeq(),
  );
}

export function selectVehicleMaintenanceEntitiesByIsReleasedFromOCF(isReleasedFromOCF: boolean) {
  return createSelector(selectAllVehicleMaintenanceEntities, (entities) =>
    entities.filter((e) => e.isReleasedFromOCF === isReleasedFromOCF).valueSeq(),
  );
}

export function selectVehicleMaintenanceEntitiesByVehicleId(vehicleId: string) {
  return createSelector(selectAllVehicleMaintenanceEntities, (entities) =>
    entities.filter((e) => e.vehicleId === vehicleId).valueSeq(),
  );
}

export function selectVehicleMaintenanceEntitiesByCompanyId(companyId: string) {
  return createSelector(selectAllVehicleMaintenanceEntities, (entities) =>
    entities.filter((e) => e.companyId === companyId).valueSeq(),
  );
}
//#endregion Selectors
